import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  X, ArrowRight, ArrowLeft, CreditCard, ShieldCheck, 
  Search, Clock, FileSpreadsheet, Zap, TrendingUp, 
  CheckCircle, Target, AlertCircle, Rocket, BarChart3,
  Percent, Lock, Unlock, MousePointer
} from 'lucide-react';
import CheckoutModal from '../components/landing/CheckoutModal';
import AfterPaymentBlock from '../components/catalog/AfterPaymentBlock';
import { usePrices } from '../context/PricesContext';

// Software screenshot
const SOFTWARE_IMAGE = 'https://customer-assets.emergentagent.com/job_wholesale-expert/artifacts/y2oea802_image.png';

// Problem points
const problemPoints = [
  'Открыть ASIN',
  'Посмотреть на красный / зелёный замок',
  'Потратить ~10–15 секунд на товар',
];

// What software does
const softwareFeatures = [
  { icon: FileSpreadsheet, text: 'Принимает любое количество ASIN-ов' },
  { icon: Clock, text: 'Проверяет всё за ~10 минут' },
  { icon: MousePointer, text: 'Нажимает кнопку Request Approval' },
  { icon: FileSpreadsheet, text: 'Формирует удобный Excel-отчёт' },
];

// Output results
const outputResults = [
  { icon: Unlock, text: 'Какие товары уже открыты', color: 'emerald' },
  { icon: MousePointer, text: 'Какие открываются по кнопке Request Approval', color: 'blue' },
  { icon: FileSpreadsheet, text: 'Какие бренды открываются простым документом', color: 'violet' },
  { icon: Lock, text: 'Какие действительно требуют инвойс', color: 'amber' },
];

// Real stats
const realStats = [
  { value: '~40%', label: 'брендов открываются с формальным документом', color: 'emerald' },
  { value: '~15%', label: 'брендов открываются сразу по Request Approval', color: 'blue' },
];

// For whom
const forWhom = [
  'Работаете с wholesale / OA',
  'Анализируете большие списки товаров',
  'Хотите быстрее расширять ассортимент',
  'Не хотите терять время на ручную проверку',
];

// Not for
const notFor = [
  'У вас 5–10 товаров',
  'Вы ещё не анализируете ассортимент',
  'Вам не важно масштабирование',
];

// Results
const finalResults = [
  'Тратите в разы меньше времени на ресёрч',
  'Не отсекаете товары из-за ложного статуса «закрыт»',
  'Находите больше открываемых брендов',
  'Увеличиваете ассортимент минимум в 1.5 раза',
];

// Image Modal Component
const ImageModal = ({ isOpen, onClose, imageSrc }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm cursor-zoom-out"
      onClick={onClose}
    >
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>
      <img 
        src={imageSrc} 
        alt="Enlarged" 
        className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const AsinCheckerPage = () => {
  const [checkoutData, setCheckoutData] = useState({ isOpen: false, packageId: '', packageName: '', price: 0 });
  const [zoomedImage, setZoomedImage] = useState(null);
  const { getPrice } = usePrices();
  const price = getPrice('asin_checker');

  const handleCheckout = () => {
    setCheckoutData({ 
      isOpen: true, 
      packageId: 'asin-checker', 
      packageName: 'ASIN Checker — Софт для проверки', 
      price: price 
    });
  };

  const closeCheckout = () => {
    setCheckoutData({ isOpen: false, packageId: '', packageName: '', price: 0 });
  };

  return (
    <div className="min-h-screen bg-[#050506]">
      {/* Image Modal */}
      <ImageModal 
        isOpen={!!zoomedImage} 
        onClose={() => setZoomedImage(null)} 
        imageSrc={zoomedImage} 
      />
      
      {/* Checkout Modal */}
      <CheckoutModal 
        isOpen={checkoutData.isOpen}
        onClose={closeCheckout}
        packageId={checkoutData.packageId}
        packageName={checkoutData.packageName}
        price={checkoutData.price}
        discountMode={false}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-black font-black text-lg">A</span>
                </div>
              </div>
              <div>
                <span className="text-white font-bold text-lg group-hover:text-amber-400 transition-colors">Amazon</span>
                <span className="text-zinc-400 font-medium text-lg ml-1">Wholesale</span>
              </div>
            </Link>
            
            <Link 
              to="/catalog"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">В каталог</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Product Card */}
          <div className="relative mb-16">
            {/* Glow */}
            <div className="absolute -inset-3 bg-gradient-to-r from-cyan-500/20 via-blue-500/30 to-cyan-500/20 rounded-[2rem] blur-2xl" />
            
            {/* Card */}
            <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-[1.5rem] border border-cyan-500/30 overflow-hidden">
              
              {/* Header */}
              <div className="relative p-8 sm:p-10 bg-gradient-to-b from-cyan-500/10 to-transparent border-b border-cyan-500/20">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-xl shadow-cyan-500/30">
                      <Search className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white">ASIN Checker</h1>
                      <p className="text-cyan-300 text-lg sm:text-xl mt-1">Софт для массовой проверки товаров</p>
                    </div>
                  </div>
                  <div className="text-left lg:text-right">
                    <p className="text-5xl sm:text-6xl font-black text-cyan-400">${price}</p>
                    <p className="text-zinc-500 text-sm mt-1">единоразово</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 sm:p-10 space-y-10">
                
                {/* What is it */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <span className="text-cyan-400 font-bold text-xl">?</span>
                    </span>
                    Что это?
                  </h3>
                  <p className="text-zinc-300 text-lg sm:text-xl leading-relaxed">
                    Софт для быстрой переработки ASIN-листов и определения, какие товары 
                    <span className="text-emerald-400 font-semibold"> реально можно открыть</span> для продажи на Amazon, а какие нет.
                  </p>
                </div>

                {/* For whom - short */}
                <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                  <p className="text-zinc-400 text-base">
                    Для тех, кто: <span className="text-white">анализирует конкурентов</span> • 
                    <span className="text-white"> работает с большими прайс-листами</span> • 
                    <span className="text-white"> масштабирует ассортимент</span>
                  </p>
                </div>

                {/* Problem */}
                <div className="p-6 sm:p-8 rounded-2xl bg-red-500/5 border border-red-500/20">
                  <h3 className="text-xl sm:text-2xl font-bold text-red-400 mb-4">
                    Проблема ручной проверки
                  </h3>
                  <p className="text-zinc-300 text-base mb-4">
                    Представьте конкурента с <span className="text-white font-bold">1000+ товаров</span>. Стандартный путь:
                  </p>
                  <div className="space-y-2 mb-4">
                    {problemPoints.map((point, i) => (
                      <div key={i} className="flex items-center gap-3 text-zinc-400 text-sm">
                        <span className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 text-xs font-bold">{i + 1}</span>
                        {point}
                      </div>
                    ))}
                  </div>
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-red-300 text-sm">
                      👉 1000 товаров = <span className="font-bold">3–4 часа</span> монотонной работы
                    </p>
                    <p className="text-red-300 text-sm mt-1">
                      👉 И при этом замочек часто <span className="font-bold">вводит в заблуждение</span>
                    </p>
                  </div>
                </div>

                {/* Important fact */}
                <div className="p-6 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <h4 className="text-amber-400 font-bold text-lg mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Важный факт
                  </h4>
                  <p className="text-zinc-300 text-base">
                    Статус «закрыт» <span className="text-red-400 font-semibold">не означает</span>, что товар невозможно открыть.
                  </p>
                  <ul className="mt-3 space-y-1 text-zinc-400 text-sm">
                    <li>• Часть брендов открываются через <span className="text-white">Request Approval</span></li>
                    <li>• Часть — по формальной проверке (пустой документ)</li>
                    <li>• Часть выглядят закрытыми, но <span className="text-emerald-400">фактически доступны</span></li>
                  </ul>
                </div>

                {/* Software Screenshot */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <BarChart3 className="w-7 h-7 text-cyan-400" />
                    Как выглядит софт
                  </h3>
                  <div 
                    className="relative cursor-zoom-in group"
                    onClick={() => setZoomedImage(SOFTWARE_IMAGE)}
                  >
                    <div className="absolute -inset-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all" />
                    <img 
                      src={SOFTWARE_IMAGE} 
                      alt="ASIN Checker Interface" 
                      className="relative rounded-xl border-2 border-cyan-500/30 w-full h-auto shadow-2xl shadow-cyan-500/20 group-hover:scale-[1.01] transition-transform"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="px-4 py-2 rounded-full bg-black/80 text-white text-sm font-medium">
                        Нажмите чтобы увеличить
                      </div>
                    </div>
                  </div>
                </div>

                {/* What it does */}
                <div className="p-6 sm:p-8 rounded-2xl bg-cyan-500/5 border border-cyan-500/20">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-5 flex items-center gap-3">
                    <Zap className="w-6 h-6 text-cyan-400" />
                    Что делает софт
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {softwareFeatures.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50">
                        <item.icon className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                        <span className="text-zinc-300 text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Output */}
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-5">
                    Что вы получаете на выходе
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {outputResults.map((item, i) => {
                      const colors = {
                        emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                        blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                        violet: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
                        amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                      };
                      return (
                        <div key={i} className={`flex items-center gap-3 p-4 rounded-xl border ${colors[item.color]}`}>
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          <span className="text-sm">{item.text}</span>
                        </div>
                      );
                    })}
                </div>
                  <p className="mt-4 text-zinc-400 text-sm">
                    + помечает «лёгкие бренды» специальным триггером • точность до <span className="text-white font-bold">99%</span>
                  </p>
                </div>

                {/* Real Stats */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {realStats.map((stat, i) => (
                    <div key={i} className={`p-5 rounded-xl ${stat.color === 'emerald' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                      <p className={`text-4xl font-black mb-2 ${stat.color === 'emerald' ? 'text-emerald-400' : 'text-blue-400'}`}>
                        {stat.value}
                      </p>
                      <p className="text-zinc-400 text-sm">{stat.label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-zinc-500 text-sm -mt-4">
                  👉 Эти возможности <span className="text-red-400">никогда не видны</span> через красно-зелёный замок
                </p>

                {/* Example */}
                <div className="p-6 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                  <h4 className="text-white font-bold text-lg mb-4">Пример использования</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
                      <p className="text-red-400 font-semibold text-sm mb-2">Обычный путь:</p>
                      <ul className="text-zinc-400 text-sm space-y-1">
                        <li>• Отсеять половину вручную</li>
                        <li>• Потерять часы времени</li>
                        <li>• Упустить товары, которые легко открываются</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                      <p className="text-emerald-400 font-semibold text-sm mb-2">С софтом:</p>
                      <ul className="text-zinc-300 text-sm space-y-1">
                        <li>• Загружаете ASIN-ы</li>
                        <li>• Через минуты получаете Excel</li>
                        <li>• Оставляете <span className="text-emerald-400 font-bold">+50%</span> товаров</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* For / Not For */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                    <h4 className="text-emerald-400 font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Подойдёт, если вы:
                    </h4>
                    <div className="space-y-2">
                      {forWhom.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-5 rounded-xl bg-red-500/5 border border-red-500/20">
                    <h4 className="text-red-400 font-bold mb-4 flex items-center gap-2">
                      <X className="w-5 h-5" />
                      Не подойдёт, если:
                    </h4>
                    <div className="space-y-2">
                      {notFor.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-zinc-400 text-sm">
                          <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-cyan-500/10 to-cyan-900/10 border border-cyan-500/30">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <Rocket className="w-7 h-7 text-cyan-400" />
                    РЕЗУЛЬТАТ
                  </h3>
                  <div className="space-y-3">
                    {finalResults.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300 text-sm sm:text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Question */}
                <div className="text-center p-6 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <p className="text-amber-300 text-lg sm:text-xl font-medium">
                    Сколько прибыльных товаров вы уже отсеяли,<br />
                    ориентируясь только на <span className="text-red-400">красный замок</span>?
                  </p>
                </div>

                {/* After Payment Block */}
                <AfterPaymentBlock />

                {/* CTA Section */}
                <div className="text-center space-y-6 pt-8 border-t border-zinc-800">
                  {/* Price Block */}
                  <div className="inline-block p-6 sm:p-8 rounded-2xl bg-gradient-to-b from-cyan-500/10 to-cyan-900/20 border border-cyan-500/30">
                    <p className="text-5xl sm:text-6xl font-black text-cyan-400 mb-4">${price}</p>
                    
                    {/* Buy Button */}
                    <button
                      onClick={handleCheckout}
                      data-testid="asin-checker-buy-btn"
                      className="w-full flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-black text-xl rounded-2xl hover:shadow-2xl hover:shadow-cyan-500/30 hover:-translate-y-1 transition-all duration-300 mb-4"
                    >
                      <CreditCard className="w-6 h-6" />
                      <span>Купить софт</span>
                      <ArrowRight className="w-6 h-6" />
                    </button>
                    
                    <p className="text-zinc-500 text-sm">Единоразовая оплата • Доступ навсегда</p>
                  </div>
                  
                  {/* Guarantee */}
                  <div className="flex items-center justify-center gap-2 text-zinc-500 text-base">
                    <ShieldCheck className="w-5 h-5 text-emerald-400" />
                    <span>Безопасная оплата через Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back to catalog */}
          <div className="text-center">
            <Link 
              to="/catalog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Вернуться в каталог</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AsinCheckerPage;
